import { Controller } from "@hotwired/stimulus";
import { destroy } from "@rails/request.js";

export default class extends Controller {
  static values = {
    items: Array,
    options: Object,
    groups: Array,
    autoResize: Boolean,
    start: String,
    end: String,
  };

  async connect() {
    try {
      this.setLoading(true);

      const { DataSet, Timeline } = await import("vis-timeline/standalone");
      await import("vis-timeline/styles/vis-timeline-graph2d.css");

      const container = this.element;
      const items = new DataSet(this.processContent(this.itemsValue));
      const hasGroups = this.hasGroupsValue && this.groupsValue.length > 0;
      const processedGroups = hasGroups ? this.processContent(this.groupsValue) : null;

      const options = this.prepareOptions();
      if (this.autoResizeValue) {
        this.setupResizing(options);
      }

      const timelineArgs = [container, items, options];
      if (hasGroups) {
        const groups = new DataSet(processedGroups);
        timelineArgs.splice(2, 0, groups);
      }

      this.timeline = new Timeline(...timelineArgs);
      this.setLoading(false);
    } catch (error) {
      console.error("Failed to initialize timeline:", error);
      this.setLoading(false);
      this.showError("Failed to load timeline visualization.");
    }
  }

  disconnect() {
    if (this.boundResizeHandler) {
      window.removeEventListener("resize", this.boundResizeHandler);
    }

    if (this.timeline) {
      this.timeline.destroy();
      this.timeline = null;
    }
  }

  setupResizing(options) {
    this.headerElement = document.getElementById("berth-slots-timeline-header");

    if (!this.headerElement) {
      console.warn("Berth timeline header element not found, auto-resize may not work correctly");
    }

    this.calculateAndSetSize(options);

    // Debounce resize calculations for better performance
    this.boundResizeHandler = this.debounce(() => {
      if (this.timeline) {
        this.calculateAndSetSize();
        this.timeline.redraw();
      }
    }, 250);

    window.addEventListener("resize", this.boundResizeHandler);
  }

  calculateAndSetSize(optionsObj = null) {
    if (!this.headerElement) return;

    const viewportHeight = window.innerHeight;
    const headerHeight = this.headerElement.offsetHeight;
    // Min height with padding
    const timelineHeight = Math.max(300, viewportHeight - headerHeight - 20);

    if (optionsObj) {
      // eslint-disable-next-line no-param-reassign
      optionsObj.height = `${timelineHeight}px`;
    } else if (this.timeline) {
      this.timeline.setOptions({ height: `${timelineHeight}px` });
    }
  }

  processContent(dataArray) {
    return dataArray.map((data) => {
      const dataCopy = { ...data };
      if (typeof dataCopy.content === "string" && this.constructor.isHtmlString(dataCopy.content)) {
        const div = document.createElement("div");
        div.innerHTML = dataCopy.content.trim();
        dataCopy.content = div.firstChild;
      }
      return dataCopy;
    });
  }

  static isHtmlString(str) {
    return /<\/?[a-z][\s\S]*>/i.test(str);
  }

  prepareOptions() {
    const options = { ...this.optionsValue };

    if (this.hasStartValue && this.startValue.trim() !== "") {
      options.start = new Date(this.startValue);
    }

    if (this.hasEndValue && this.endValue.trim() !== "") {
      options.end = new Date(this.endValue);
    }

    if (options.editable?.remove) {
      options.onRemove = async (item, callback) => {
        try {
          this.setLoading(true);
          const response = await destroy(`/berth_availability_slots/${item.id}`);

          if (response.ok) {
            callback(item);
            this.setLoading(false);
          } else {
            callback(null);
            this.setLoading(false);
            this.showError("Failed to remove the slot. Server responded with an error.");
          }
        } catch (error) {
          console.error("Error handling slot removal:", error);
          callback(null);
          this.setLoading(false);
          this.showError("Network error when removing the slot.");
        }
      };
    }

    return options;
  }

  showError(message) {
    const errorEvent = new CustomEvent("berth-timeline:error", {
      detail: { message },
      bubbles: true,
    });
    this.element.dispatchEvent(errorEvent);

    // Fallback to console error if no event listeners
    console.error(message);
  }

  setLoading(isLoading) {
    this.element.classList.toggle("is-loading", isLoading);

    const loadingEvent = new CustomEvent("berth-timeline:loading", {
      detail: { isLoading },
      bubbles: true,
    });
    this.element.dispatchEvent(loadingEvent);
  }

  // Debounce implementation to limit function calls
  // eslint-disable-next-line class-methods-use-this
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}
